import { can } from "../../auth/can.ts";
import React from "react";
import { notification } from "../../utils/Notifications.tsx";
import { readApplicationStatuses } from "../../services/applicationStatuses.service.ts";
import { readPositions } from "../../services/position.service.ts";
import { getShifts } from "../../services/shift.service.ts";
import ApplicationStatus from "../../models/ApplicationStatuses.ts";
import { Position } from "../../models/Position.ts";
import { Shift } from "../../models/Shift.ts";
import { useDispatch, useSelector } from "react-redux";
import { setQuery } from "../../store/slices/solicitudesSlice.ts";
import { RootState } from "../../store/index.ts";
import { DatePicker } from "antd";
import locale from "antd/es/date-picker/locale/es_ES";
import dayjs, { Dayjs } from "dayjs";
import PositionAutocomplete from "../common/PositionAutocomplete.tsx";

const { RangePicker } = DatePicker;

const FiltroSolicitudes = () => {
  const { user } = useSelector((state: RootState) => state.auth);

  const initialValues = {
    status: -1,
    position: -1,
    monthlySalary: 0,
    shift: -1,
    search: "",
    start: "",
    end: "",
  };

  const dispatch = useDispatch();
  const [status, setStatus] = React.useState<ApplicationStatus[]>([]);
  const [positions, setPositions] = React.useState<Position[]>([]);
  const [shifts, setShifts] = React.useState<Shift[]>([]);
  const [filters, setFilters] = React.useState(initialValues);
  const [timer, setTimer] = React.useState<any>(null);
  const [showPills, setShowPills] = React.useState(false);
  const [dateRange, setDateRange] = React.useState<[Dayjs | null, Dayjs | null] | null>(null);

  const fetchDependencies = async () => {
    try {
      const statusResponse = await readApplicationStatuses();
      setStatus(statusResponse);

      if (can("positions.view", user)) {
        const positionResposne = await readPositions();
        setPositions(positionResposne);
      }

      const shiftsResponse = await getShifts();
      setShifts(shiftsResponse);
    } catch (error) {
      notification(
        "Error",
        "Ocurrió un error al tratar de obtener los filtros",
        "danger"
      );
    }
  };

  React.useEffect(() => {
    fetchDependencies();
    // Rango por defecto: hoy a 30 días atrás
    const end = dayjs();
    const start = end.subtract(30, "day");
    setDateRange([start, end]);
    const defaultFilters = {
      ...initialValues,
      start: start.format("YYYY-MM-DD"),
      end: end.format("YYYY-MM-DD"),
    };
    setFilters(defaultFilters);
    const queryString = buildQuery(defaultFilters);
    if (queryString) {
      dispatch(setQuery(queryString));
      setShowPills(true);
    }
  }, []);

  const buildQuery = (source: typeof filters) =>
    Object.keys(source)
      .filter((key) => {
        const val = (source as any)[key];
        if (key === "monthlySalary") return val > 0;
        if (key === "status" || key === "position" || key === "shift") {
          return val !== -1;
        }
        if (key === "search") {
          return val !== "";
        }
        if (key === "start" || key === "end") {
          return val !== "";
        }
        return false;
      })
      .map(
        (key) =>
          `${encodeURIComponent(key)}=${encodeURIComponent((source as any)[key])}`
      )
      .join("&");

  const updateFilters = () => {
    const queryString = buildQuery(filters);
    dispatch(setQuery(queryString));
  };

  const handleDateRangeChange = (
    values: [Dayjs | null, Dayjs | null] | null,
  ) => {
    if (!values || !values[0] || !values[1]) {
      setDateRange(null);
      const updated = { ...filters, start: "", end: "" };
      setFilters(updated);
      const queryString = buildQuery(updated);
      dispatch(setQuery(queryString));
      setShowPills(updated.search !== "" || updated.status !== -1);
      return;
    }

    const [start, end] = values;
    setDateRange(values);
    const updated = {
      ...filters,
      start: start.format("YYYY-MM-DD"),
      end: end.format("YYYY-MM-DD"),
    };
    setFilters(updated);
    const queryString = buildQuery(updated);
    dispatch(setQuery(queryString));
    setShowPills(true);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setFilters((prevFilters) => ({ ...prevFilters, search: newValue })); // Actualización inmediata
    if (newValue === "") {
      setShowPills(false);
    } else {
      setShowPills(true);
    }
    if (timer) {
      clearTimeout(timer);
    }
    setTimer(
      setTimeout(() => {
        const updatedFilters = { ...filters, search: newValue };
        const queryString = buildQuery(updatedFilters);
        dispatch(setQuery(queryString));

        setTimer(null);
      }, 500)
    );
  };

  const getStatusDisplayName = (statusName: string) => {
    return statusName === "Solicitado" ? "Candidato" : statusName;
  };

  const STATUS_DISPLAY_ORDER = [
    "Solicitado",
    "Entrevistado",
    "Aceptado",
    "Entrevista con Lider",
    "Pre-Colaborador",
    "Rechazado",
    "Cartera",
  ];

  const getStatusColors = (statusName: string) => {
    const colors: Record<string, { main: string; light: string }> = {
      Solicitado: { main: "#2563eb", light: "#dbeafe" },
      Entrevistado: { main: "#7a6200", light: "#fffbeb" },
      Aceptado: { main: "#16a34a", light: "#dcfce7" },
      "Entrevista con Lider": { main: "#0891b2", light: "#cffafe" },
      "Pre-Colaborador": { main: "#ea580c", light: "#ffedd5" },
      Rechazado: { main: "#dc2626", light: "#fee2e2" },
      Cartera: { main: "#6b7280", light: "#f3f4f6" },
    };
    return colors[statusName] || { main: "#6b7280", light: "#f3f4f6" };
  };

  const sortedStatus = React.useMemo(() => {
    return [...status]
      .filter((s) => s.name !== "Colaborador")
      .sort((a, b) => {
        const idxA = STATUS_DISPLAY_ORDER.indexOf(a.name);
        const idxB = STATUS_DISPLAY_ORDER.indexOf(b.name);
        if (idxA === -1 && idxB === -1) return 0;
        if (idxA === -1) return 1;
        if (idxB === -1) return -1;
        return idxA - idxB;
      });
  }, [status]);

  const getStatusButtonStyle = (
    statusName: string,
    isSelected: boolean,
    isTodos?: boolean
  ): React.CSSProperties => {
    if (isTodos) {
      return {
        background: isSelected
          ? "linear-gradient(135deg, #374151 0%, #1f2937 100%)"
          : "linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)",
        color: isSelected ? "#ffffff" : "#374151",
        border: "none",
        borderRadius: "16px",
        padding: "0.5rem 1rem",
        fontSize: "0.875rem",
        fontWeight: 500,
        minWidth: "80px",
        transition: "all 0.3s ease-in-out",
        boxShadow: isSelected
          ? "0 4px 12px rgba(55, 65, 81, 0.35)"
          : "0 2px 4px rgba(0, 0, 0, 0.08)",
        transform: isSelected ? "scale(1.05)" : "scale(1)",
      };
    }
    const { main, light } = getStatusColors(statusName);
    return {
      background: isSelected
        ? `linear-gradient(135deg, ${main} 0%, ${main}dd 100%)`
        : `linear-gradient(135deg, ${light} 0%, ${light}ee 100%)`,
      color: isSelected ? "#ffffff" : main,
      border: isSelected ? "none" : `1px solid ${main}40`,
      borderRadius: "16px",
      padding: "0.5rem 1rem",
      fontSize: "0.875rem",
      fontWeight: 500,
      minWidth: "80px",
      transition: "all 0.3s ease-in-out",
      boxShadow: isSelected
        ? `0 4px 12px ${main}50, 0 2px 4px rgba(0,0,0,0.1)`
        : "0 2px 4px rgba(0, 0, 0, 0.08)",
      transform: isSelected ? "scale(1.05)" : "scale(1)",
    };
  };

  const selectedFilters = (filters: any) => {
    //return the propertie, the value and the value name in one single object
    let selected: any = [];
    for (const key in filters) {
      if (filters[key] !== -1 && filters[key] !== "" && filters[key] !== 0) {
        let value = filters[key];
        let valueName = "";
        let keyName = "";
        switch (key) {
          case "status":
            keyName = "Estatus";
            const statusName = status.find((item) => item.id === value)?.name || "";
            valueName = getStatusDisplayName(statusName);
            break;
          case "position":
            keyName = "Puesto";
            valueName = positions.find((item) => item.id === value)?.name || "";
            break;
          case "shift":
            keyName = "Turno";
            valueName = shifts.find((item) => item.id === value)?.name || "";
            break;
          case "search":
            keyName = "Búsqueda";
            valueName = value;
            break;
          case "start":
          case "end":
            // El periodo se muestra como un solo pill, así que se maneja más abajo
            continue;
          default:
            keyName = key;
            valueName = value;
            break;
        }
        selected.push({ key, value, valueName, keyName });
      }
    }

    if (selected.length === 0) setShowPills(false);
    // Agregar pill de periodo si ambos valores existen
    if (filters.start && filters.end) {
      const startLabel = dayjs(filters.start).format("DD/MM/YYYY");
      const endLabel = dayjs(filters.end).format("DD/MM/YYYY");
      selected.push({
        key: "periodo",
        value: `${filters.start}|${filters.end}`,
        valueName: `${startLabel} - ${endLabel}`,
        keyName: "Periodo",
      });
    }

    return selected;
  }

  const removeFilter = (key: string) => {
    if (key === "periodo") {
      const updatedFilters = { ...filters, start: "", end: "" };
      setFilters(updatedFilters);
      setDateRange(null);
      const queryString = buildQuery(updatedFilters);
      dispatch(setQuery(queryString));
      setShowPills(
        updatedFilters.search !== "" ||
          updatedFilters.status !== -1 ||
          updatedFilters.position !== -1 ||
          updatedFilters.shift !== -1 ||
          updatedFilters.monthlySalary > 0
      );
      return;
    }

    const updatedFilters = { ...filters, [key]: (initialValues as any)[key] };
    setFilters(updatedFilters);
    const queryString = buildQuery(updatedFilters);
    dispatch(setQuery(queryString));
  };

  const handleStatusButtonClick = (statusId: number) => {
    const newFilters = { ...filters, status: statusId };
    setFilters(newFilters);
    const queryString = buildQuery(newFilters);
    dispatch(setQuery(queryString));
    setShowPills(statusId !== -1);
  };

  return (
    <div>
      {!user.branch && status.length > 0 && (
        <div
          className="d-flex flex-wrap align-items-center justify-content-center mb-3"
          style={{
            gap: "0.75rem",
            padding: "0.85rem 1rem",
          }}
        >
          <button
            type="button"
            className="border-0 text-sm font-medium"
            style={getStatusButtonStyle("", filters.status === -1, true)}
            onMouseEnter={(e) => {
              if (filters.status !== -1) {
                e.currentTarget.style.transform = "scale(1.02)";
                e.currentTarget.style.boxShadow =
                  "0 4px 8px rgba(0, 0, 0, 0.12)";
              }
            }}
            onMouseLeave={(e) => {
              if (filters.status !== -1) {
                e.currentTarget.style.transform = "scale(1)";
                e.currentTarget.style.boxShadow =
                  "0 2px 4px rgba(0, 0, 0, 0.08)";
              }
            }}
            onClick={() => handleStatusButtonClick(-1)}
          >
            Todos
          </button>
          {sortedStatus.map((item) => {
            const isSelected = filters.status === item.id;
            return (
              <button
                key={item.id}
                type="button"
                className="border-0 text-sm font-medium"
                style={getStatusButtonStyle(item.name, isSelected)}
                onMouseEnter={(e) => {
                  if (!isSelected) {
                    e.currentTarget.style.transform = "scale(1.02)";
                    e.currentTarget.style.boxShadow =
                      "0 4px 8px rgba(0, 0, 0, 0.12)";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isSelected) {
                    e.currentTarget.style.transform = "scale(1)";
                    e.currentTarget.style.boxShadow =
                      "0 2px 4px rgba(0, 0, 0, 0.08)";
                  }
                }}
                onClick={() => handleStatusButtonClick(item.id)}
              >
                {getStatusDisplayName(item.name)}
              </button>
            );
          })}
        </div>
      )}
      <div className="input-group mb-2">
        {!(can("files.upload", user) && !can("applications.create_internal", user)) && (
          <button
            className="btn btn-white dropdown-toggle"
            type="button"
            data-bs-toggle="dropdown"
            style={{ borderRadius: "999px 0 0 999px" }}
          >
            <span className="d-none d-md-inline">Filtrar </span>
            <span className="d-inline d-md-none">
              <i className="fa fa-filter"></i>
            </span>
            <b className="caret"></b>
          </button>
        )}
        <div className="dropdown-menu p-4" onClick={(e) => e.stopPropagation()}>
          <PositionAutocomplete
            positions={positions}
            value={filters.position}
            onChange={(positionId) =>
              setFilters({ ...filters, position: positionId })
            }
            label="Puesto"
            placeholder="Buscar puesto..."
            inline
          />

          <label className="mt-2 mb-0">Turno:</label>
          <select
            className="form-select mt-1"
            id="shift"
            name="shift"
            onChange={(e) =>
              setFilters({ ...filters, shift: Number(e.target.value) })
            }
            value={filters.shift}
          >
            <option value={-1} disabled>
              Todos
            </option>
            {shifts.map((item) => {
              return (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              );
            })}
          </select>

          <label className="mt-2">Expectativa Salarial:</label>
          <input
            type="number"
            className="form-control mt-1"
            id="monthly_salary"
            name="monthlySalary"
            placeholder="Expectativa"
            onChange={(e) =>
              setFilters({ ...filters, monthlySalary: Number(e.target.value) })
            }
            value={filters.monthlySalary}
          />

          <div className="row mt-3 mt-1">
            <div className="col">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setFilters({
                    ...initialValues,
                    start: filters.start,
                    end: filters.end,
                  });
                  dispatch(setQuery(""));
                  setShowPills(false);
                }}
                className="btn btn-amarillo d-block w-100 btn-lg fs-14px"
              >
                Limpiar
              </button>
            </div>
            <div className="col">
              <button
                // type="submit"
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  updateFilters();
                  setShowPills(true);
                }}
                className="btn btn-azul d-block w-100 btn-lg fs-14px"
              >
                Aplicar
              </button>
            </div>
          </div>
        </div>

        <div className="flex-fill position-relative">
          <div className="input-group">
            <div
              className="input-group-text position-absolute top-0 bottom-0 bg-none border-0 start-0"
              style={{ zIndex: 10 }}
            >
              {timer ? (
                <i className="fa fa-spinner fa-spin"></i>
              ) : (
                <i className="fa fa-search opacity-5"></i>
              )}
            </div>
            <input
              type="text"
              className="form-control px-35px bg-light"
              placeholder="Ingresa un nombre..."
              id="name"
              name="name"
              onChange={(e) => handleSearchChange(e)}
              value={filters.search}
              style={{ borderRadius: "0 999px 999px 0" }}
            />
          </div>
        </div>
        <div className="ms-2" style={{ minWidth: "260px" }}>
          <RangePicker
            value={dateRange || undefined}
            onChange={handleDateRangeChange}
            locale={locale}
            format="DD/MM/YYYY"
            allowClear
            style={{
              width: "100%",
              borderRadius: "999px",
            }}
          />
        </div>
      </div>
      <div className="mb-3">
        {
        showPills && selectedFilters(filters).map((filter: any) => (
          <div className="badge bg-primary text-white fs-6 me-2 position-relative pe-4">
            {filter.keyName}: {filter.valueName}
            <button
              type="button"
              className="btn-close btn-close-white position-absolute top-0 end-0 mt-1 me-1 ps-1s"
              aria-label="Close"
              onClick={() => removeFilter(filter.key)}
            ></button>
          </div>
        ))
      }
      </div>
    </div>
  );
};

export default FiltroSolicitudes;
